import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import './FlowMoversPanel.css';
import { LanguageContext } from '../../contexts/LanguageContext';
import {
  fetchFlowMovers, MEASURE_KEYS, Mover, MoverCluster, MoverMeasures, MoverPattern, MoverSide, MoversResponse,
  RATIO_MEASURE, RatioMode, SortMode, Z_MEASURE, ZMode,
} from '../../services/flowMoversService';
import { raiseAlert } from '../../utils/alertRaise';
import { appendAlertLog } from '../../utils/alertRuntime';

/**
 * Strike Movers — the right-hand panel of the Flow Tape. Lists the strikes whose order-flow
 * changed most inside a time window (sold and bought), lets the owner set what "qualifies"
 * (minimum change, |z| under the chosen lens, spike ratio, the chosen ratio ×, side), shows the
 * strike clusters that moved together, and raises an in-app alert when a NEW qualifying mover or
 * pattern appears.
 *
 * The readings shown are automatic readings of data, not recommendations; the copy keeps to the
 * data words "sold"/"bought" and names no vendor.
 */

export interface FlowMoversPanelProps {
  underlying: string;
  expiry: string;
  session: string;
  isLive: boolean;
  /** The tape's last successful series poll; a change here re-queries the movers. */
  lastTick: number;
  ladder: { key: string; strike: number }[];
  selected: string[];
  /** Add the strike to the tape's selection (keep the others) and focus it. */
  onPick: (key: string) => void;
  /** Plot only this strike. */
  onPickOnly: (key: string) => void;
  /** Plot exactly these strikes (a cluster) and focus the first. */
  onPickMany?: (keys: string[]) => void;
}

type SideFilter = 'C' | 'P' | 'CP';
interface Prefs {
  open: boolean;
  mode: 'rel' | 'abs';
  /** Window length for the relative mode (minutes). */
  lenMin: number;
  /** Absolute window as minutes after 09:30 ET. */
  from: number;
  to: number;
  minChange: number;
  minZ: number;
  minSpike: number;
  minRatio: number;
  zmode: ZMode;
  ratio: RatioMode;
  sides: SideFilter;
  sort: SortMode;
  clusters: boolean;
  alerts: boolean;
  sound: boolean;
  push: boolean;
}

const PREFS_KEY = 'ft_movers';
const SEEN_KEY = 'ft_movers_seen';
const RTH_MIN = 390;           // 09:30 → 16:00 ET
const OPEN_MIN = 9 * 60 + 30;  // minutes after midnight ET
const STEP = 5;
const QUICK = [15, 30, 60, 120];
const POLL_MS = 10_000;
const SEEN_TTL_MS = 30 * 60_000;
const STORM_WINDOW_MS = 5 * 60_000;
const STORM_MAX = 6;
const STRONG_IMB = 0.6;
const TIP_HOVER_MS = 320;
const TIP_PRESS_MS = 450;
const ZMODES: ZMode[] = ['all', 'trim95', 'mad', 'cluster'];
const RATIOS: RatioMode[] = ['avg_all', 'avg95', 'p95', 'median', 'own', 'vol'];
const SORTS: SortMode[] = ['magnitude', 'z', 'spike', 'ratio', 'share'];

const DEFAULT_PREFS: Prefs = {
  open: true, mode: 'rel', lenMin: 30, from: 0, to: RTH_MIN, minChange: 0, minZ: 0, minSpike: 0, minRatio: 0,
  zmode: 'all', ratio: 'avg_all', sides: 'CP', sort: 'magnitude', clusters: true, alerts: false, sound: false, push: false,
};

const loadPrefs = (): Prefs => {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const p = JSON.parse(raw);
    const m = { ...DEFAULT_PREFS, ...(p && typeof p === 'object' ? p : {}) } as Prefs;
    if (!ZMODES.includes(m.zmode)) m.zmode = 'all';
    if (!RATIOS.includes(m.ratio)) m.ratio = 'avg_all';
    if (!SORTS.includes(m.sort)) m.sort = 'magnitude';
    return m;
  } catch { return DEFAULT_PREFS; }
};

const COPY = {
  en: {
    title: 'Strike Movers', sub: 'biggest flow changes in the window',
    collapse: 'Collapse', expand: 'Expand Strike Movers',
    window: 'Window', session: 'Session', quickMin: (n: number) => `${n}m`,
    minChange: 'Min change', minZ: 'Min |z|', minSpike: 'Min spike', minRatio: 'Min ratio', contracts: 'contracts', times: '×',
    lens: 'Lens', ratio: 'Ratio',
    zmode: { all: 'All strikes', trim95: '95% cluster', mad: 'Robust (MAD)', cluster: 'Own cluster' } as Record<ZMode, string>,
    ratioMode: { avg_all: 'vs avg (all)', avg95: 'vs avg (95%)', p95: 'vs 95th pct', median: 'vs median', own: 'vs own normal', vol: 'volume vs avg' } as Record<RatioMode, string>,
    calls: 'Calls', puts: 'Puts', both: 'Both', sort: 'Sort',
    sortBy: { magnitude: 'Change', z: 'z', spike: 'Spike', ratio: 'Ratio', share: 'Share' } as Record<SortMode, string>,
    alertMe: 'Alert me', sound: 'Sound', push: 'OS push', pushNeedsPermission: 'Allow notifications in the browser first',
    alertHint: 'A new qualifying strike, cluster or pattern raises one in-app alert',
    sold: 'Sold', bought: 'Bought', none: 'No strikes qualify in this window.', loading: 'Reading the tape…',
    noData: 'No order-flow in this window yet.', tsdbDown: 'History store unavailable.', err: 'Movers unavailable right now.',
    strikes: 'strikes', mean: 'mean', sd: 'sd', intervals: 'intervals', peak: 'peak',
    callsVsPuts: 'calls vs puts', aboveVsBelow: 'above vs below ref',
    patterns: 'Patterns', range_or_up_lean: 'Premium sold on both sides (call above, put below): range or upward lean',
    breakout_lean: 'Premium bought on both sides: breakout lean',
    cluster_sold: (lo: string, hi: string, imb: string) => `Cluster ${lo}–${hi} net sold (${imb})`,
    cluster_bought: (lo: string, hi: string, imb: string) => `Cluster ${lo}–${hi} net bought (${imb})`,
    clusters: 'Clusters', clustersHint: 'click: plot the whole cluster', clusterCols: { range: 'Range', net: 'Net', share: 'Share', imb: 'Imb', z: 'z' },
    bias: { calls: 'calls', puts: 'puts', mixed: 'mixed' } as Record<string, string>, aboveRef: 'above ref', belowRef: 'below ref', strikesN: (n: number) => `${n} strikes`,
    caption: 'An automatic reading of data, not a recommendation', clickHint: 'click: plot · double-click: plot only',
    qualifies: 'meets your thresholds', colStrike: 'Strike', colChange: 'Change', colZ: 'z', colSpike: 'Spike', colRatio: 'Ratio', colImb: 'Imb', colPeak: 'Peak',
    measure: {
      z_all: 'z · all strikes', z_trim95: 'z · 95% cluster', z_mad: 'z · robust (MAD)', z_cluster: 'z · own cluster',
      ratio_avg_all: 'ratio vs avg (all)', ratio_avg95: 'ratio vs avg (95%)', ratio_p95: 'ratio vs 95th pct', ratio_median: 'ratio vs median',
      rel_own: 'vs own normal', share_total: 'share of total', imbalance: 'imbalance (buy − sell)', vol_ratio_all: 'volume vs avg',
    } as Record<keyof MoverMeasures, string>,
    tipCluster: 'cluster', tipSpike: 'spike (peak vs normal)', tipPeak: 'peak interval', tipChange: 'net change',
    alertTitle: (u: string) => `Strike Movers · ${u}`,
    moverMsg: (u: string, s: string, dir: string, amt: string, min: number, spk: string, z: string, r: string) => `${u} ${s} ${dir} ${amt} in ${min} min · spike ${spk} · z ${z} · ratio ${r}`,
    patternMsg: (c: string, p: string, dir: string) => `${c} + ${p} ${dir} — premium ${dir} both sides`,
    summaryMsg: (n: number) => `${n} more qualifying movers in the last 5 min — alerts paused briefly`,
    dirWord: { sold: 'sold', bought: 'bought' } as Record<string, string>,
  },
  ar: {
    title: 'الضربات الأكثر حركة', sub: 'أكبر تغيّرات التدفق في النافذة',
    collapse: 'طيّ', expand: 'فتح الضربات الأكثر حركة',
    window: 'النافذة', session: 'الجلسة كاملة', quickMin: (n: number) => `${n} د`,
    minChange: 'أدنى تغيّر', minZ: 'أدنى |z|', minSpike: 'أدنى قفزة', minRatio: 'أدنى نسبة', contracts: 'عقد', times: '×',
    lens: 'العدسة', ratio: 'النسبة',
    zmode: { all: 'كل الضربات', trim95: 'عنقود 95%', mad: 'متين (MAD)', cluster: 'عنقوده' } as Record<ZMode, string>,
    ratioMode: { avg_all: 'إلى المتوسط (الكل)', avg95: 'إلى متوسط 95%', p95: 'إلى المئين 95', median: 'إلى الوسيط', own: 'إلى معتاده', vol: 'الحجم إلى المتوسط' } as Record<RatioMode, string>,
    calls: 'كول', puts: 'بوت', both: 'الكل', sort: 'ترتيب',
    sortBy: { magnitude: 'التغيّر', z: 'z', spike: 'القفزة', ratio: 'النسبة', share: 'الحصة' } as Record<SortMode, string>,
    alertMe: 'نبّهني', sound: 'صوت', push: 'إشعار النظام', pushNeedsPermission: 'اسمح بالإشعارات في المتصفح أولاً',
    alertHint: 'ضربة أو عنقود أو نمط جديد يستوفي الشروط يرفع تنبيهاً واحداً داخل المنصة',
    sold: 'مُباع', bought: 'مُشترى', none: 'لا ضربات تستوفي الشروط في هذه النافذة.', loading: 'جارٍ قراءة الشريط…',
    noData: 'لا تدفق في هذه النافذة بعد.', tsdbDown: 'مخزن السجل غير متاح.', err: 'التحركات غير متاحة الآن.',
    strikes: 'ضربة', mean: 'المتوسط', sd: 'الانحراف', intervals: 'فترة', peak: 'الذروة',
    callsVsPuts: 'كول مقابل بوت', aboveVsBelow: 'فوق مقابل تحت المرجع',
    patterns: 'الأنماط', range_or_up_lean: 'علاوة مُباعة على الجانبين (كول فوق، بوت تحت): نطاق أو ميل صعودي',
    breakout_lean: 'علاوة مُشتراة على الجانبين: ميل للاختراق',
    cluster_sold: (lo: string, hi: string, imb: string) => `عنقود ${lo}–${hi} مُباع صافيًا (${imb})`,
    cluster_bought: (lo: string, hi: string, imb: string) => `عنقود ${lo}–${hi} مُشترى صافيًا (${imb})`,
    clusters: 'العناقيد', clustersHint: 'نقرة: ارسم العنقود كاملاً', clusterCols: { range: 'النطاق', net: 'الصافي', share: 'الحصة', imb: 'الاختلال', z: 'z' },
    bias: { calls: 'كول', puts: 'بوت', mixed: 'مختلط' } as Record<string, string>, aboveRef: 'فوق المرجع', belowRef: 'تحت المرجع', strikesN: (n: number) => `${n} ضربات`,
    caption: 'قراءة آلية للبيانات وليست توصية', clickHint: 'نقرة: ارسم · نقرتان: ارسم هذه فقط',
    qualifies: 'تستوفي حدودك', colStrike: 'الضربة', colChange: 'التغيّر', colZ: 'z', colSpike: 'القفزة', colRatio: 'النسبة', colImb: 'الاختلال', colPeak: 'الذروة',
    measure: {
      z_all: 'z · كل الضربات', z_trim95: 'z · عنقود 95%', z_mad: 'z · متين (MAD)', z_cluster: 'z · عنقوده',
      ratio_avg_all: 'النسبة إلى المتوسط (الكل)', ratio_avg95: 'النسبة إلى متوسط 95%', ratio_p95: 'النسبة إلى المئين 95', ratio_median: 'النسبة إلى الوسيط',
      rel_own: 'إلى معتاده', share_total: 'الحصة من الإجمالي', imbalance: 'الاختلال (شراء − بيع)', vol_ratio_all: 'الحجم إلى المتوسط',
    } as Record<keyof MoverMeasures, string>,
    tipCluster: 'العنقود', tipSpike: 'القفزة (الذروة إلى المعتاد)', tipPeak: 'فترة الذروة', tipChange: 'صافي التغيّر',
    alertTitle: (u: string) => `الضربات الأكثر حركة · ${u}`,
    moverMsg: (u: string, s: string, dir: string, amt: string, min: number, spk: string, z: string, r: string) => `${u} ${s} ${dir} ${amt} خلال ${min} دقيقة · قفزة ${spk} · z ${z} · نسبة ${r}`,
    patternMsg: (c: string, p: string, dir: string) => `${c} + ${p} ${dir === 'بيع' ? 'مُباعة' : 'مُشتراة'} — علاوة ${dir === 'بيع' ? 'مُباعة' : 'مُشتراة'} على الجانبين`,
    summaryMsg: (n: number) => `${n} تحركات أخرى استوفت الشروط خلال 5 دقائق — التنبيهات متوقفة مؤقتاً`,
    dirWord: { sold: 'بيع', bought: 'شراء' } as Record<string, string>,
  },
};

const nf = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const isNum = (v: number | null | undefined): v is number => v !== null && v !== undefined && Number.isFinite(v);
const fmtInt = (v: number | null | undefined) => (isNum(v) ? nf.format(Math.round(v)) : '—');
const fmtSigned = (v: number | null | undefined) => (isNum(v) ? (v > 0 ? '+' : v < 0 ? '−' : '') + nf.format(Math.abs(Math.round(v))) : '—');
const fmt1 = (v: number | null | undefined, suffix = '') => (isNum(v) ? v.toFixed(1) + suffix : '—');
const fmtImb = (v: number | null | undefined) => (isNum(v) ? (v > 0 ? '+' : v < 0 ? '−' : '') + Math.abs(v).toFixed(2) : '—');
const fmtPct = (v: number | null | undefined) => (isNum(v) ? (v * 100).toFixed(v * 100 >= 10 ? 0 : 1) + '%' : '—');
const hhmm = (minAfterOpen: number) => { const m = OPEN_MIN + minAfterOpen; return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`; };
const etTime = (ms: number | null) => (ms === null ? '—' : new Date(ms).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/New_York' }));
const strikeKey = (strike: number) => String(Number(strike));
const strikeLabel = (strike: number, side: MoverSide) => `${strikeKey(strike)}${side}`;
const signClass = (v: number | null | undefined) => (isNum(v) ? (v < 0 ? 'neg' : v > 0 ? 'pos' : '') : '');

/** Minutes after 09:30 ET right now, rounded up to the 5-min grid and clamped to the session. */
function nowAfterOpen(): number {
  try {
    const s = new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/New_York' }).format(new Date());
    const [h, m] = s.split(':').map(Number);
    const v = (h % 24) * 60 + m - OPEN_MIN;
    return Math.max(STEP, Math.min(RTH_MIN, Math.ceil(v / STEP) * STEP));
  } catch { return RTH_MIN; }
}

type SeenMap = Record<string, number>;
const loadSeen = (): SeenMap => { try { const r = sessionStorage.getItem(SEEN_KEY); const p = r ? JSON.parse(r) : null; return p && typeof p === 'object' ? p : {}; } catch { return {}; } };
const saveSeen = (m: SeenMap) => { try { sessionStorage.setItem(SEEN_KEY, JSON.stringify(m)); } catch { /* best effort */ } };

/** A centred −1..1 bar: sell (negative) fills to the left in red, buy to the right in green. */
function ImbBar({ v, wide }: { v: number | null; wide?: boolean }) {
  const x = isNum(v) ? Math.max(-1, Math.min(1, v)) : 0;
  const w = Math.abs(x) * 50;
  return (
    <span className={'fm-imb' + (wide ? ' is-wide' : '') + (isNum(v) ? '' : ' is-null')} aria-hidden>
      <i className={x < 0 ? 'neg' : 'pos'} style={x < 0 ? { right: '50%', width: `${w}%` } : { left: '50%', width: `${w}%` }} />
    </span>
  );
}

interface Tip { key: string; x: number; y: number }

export default function FlowMoversPanel({ underlying, expiry, session, isLive, lastTick, ladder, selected, onPick, onPickOnly, onPickMany }: FlowMoversPanelProps) {
  const langCtx = useContext(LanguageContext);
  const isArabic = langCtx?.language === 'ar';
  const c = isArabic ? COPY.ar : COPY.en;

  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);
  useEffect(() => { try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch { /* noop */ } }, [prefs]);
  const set = useCallback(<K extends keyof Prefs>(k: K, v: Prefs[K]) => setPrefs(p => ({ ...p, [k]: v })), []);

  // ── window ───────────────────────────────────────────────
  const [nowMark, setNowMark] = useState<number>(() => (isLive ? nowAfterOpen() : RTH_MIN));
  useEffect(() => { setNowMark(isLive ? nowAfterOpen() : RTH_MIN); }, [isLive, lastTick, session]);
  const win = useMemo(() => {
    if (prefs.mode === 'abs') { const from = Math.max(0, Math.min(prefs.from, RTH_MIN - STEP)); const to = Math.max(from + STEP, Math.min(prefs.to, RTH_MIN)); return { from, to }; }
    const to = nowMark; const from = Math.max(0, to - prefs.lenMin);
    return { from, to };
  }, [prefs.mode, prefs.from, prefs.to, prefs.lenMin, nowMark]);
  const winMin = win.to - win.from;

  // ── data ─────────────────────────────────────────────────
  const [data, setData] = useState<MoversResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => { if (!isLive || !prefs.open) return; const id = setInterval(() => setTick(t => t + 1), POLL_MS); return () => clearInterval(id); }, [isLive, prefs.open]);
  const sides: MoverSide[] = prefs.sides === 'CP' ? ['C', 'P'] : [prefs.sides];
  useEffect(() => {
    if (!underlying || !session || !prefs.open) return;
    let dead = false; setLoading(true);
    // Thresholds stay client-side (sent as 0) so a threshold change never re-queries; the lens / ratio / sort
    // go to the backend so its `z`, ranking and top-N agree with what the panel shows.
    fetchFlowMovers({ underlying, expiry, session, from: hhmm(win.from), to: hhmm(win.to), windowMin: winMin, minChange: 0, minZ: 0, minSpike: 0, minRatio: 0, zmode: prefs.zmode, ratio: prefs.ratio, sort: prefs.sort, sides, top: 50 })
      .then(d => { if (!dead) { setData(d); setLoading(false); } });
    return () => { dead = true; };
  }, [underlying, expiry, session, win.from, win.to, prefs.sides, prefs.open, prefs.zmode, prefs.ratio, prefs.sort, lastTick, tick]);

  // ── the chosen lens / ratio, with the older flat fields as fallback ──
  const zOf = useCallback((m: Mover) => m.measures[Z_MEASURE[prefs.zmode]] ?? (prefs.zmode === 'all' ? m.z : null), [prefs.zmode]);
  const ratioOf = useCallback((m: Mover) => m.measures[RATIO_MEASURE[prefs.ratio]] ?? null, [prefs.ratio]);
  const magOf = (m: Mover) => m.magnitude ?? (m.change_net === null ? 0 : Math.abs(m.change_net));

  const qualifies = useCallback((m: Mover) => {
    if (prefs.minChange > 0 && magOf(m) < prefs.minChange) return false;
    if (prefs.minZ > 0) { const z = zOf(m); if (z === null || Math.abs(z) < prefs.minZ) return false; }
    if (prefs.minSpike > 0 && (m.spike_ratio === null || m.spike_ratio < prefs.minSpike)) return false;
    if (prefs.minRatio > 0) { const r = ratioOf(m); if (r === null || r < prefs.minRatio) return false; }
    return true;
  }, [prefs.minChange, prefs.minZ, prefs.minSpike, prefs.minRatio, zOf, ratioOf]);

  const rows = useMemo(() => {
    const all = (data?.movers || []).filter(m => sides.includes(m.side));
    const sortVal = (m: Mover) =>
      prefs.sort === 'z' ? Math.abs(zOf(m) ?? 0)
        : prefs.sort === 'spike' ? (m.spike_ratio ?? 0)
          : prefs.sort === 'ratio' ? (ratioOf(m) ?? 0)
            : prefs.sort === 'share' ? (m.measures.share_total ?? 0)
              : magOf(m);
    const by = (a: Mover, b: Mover) => sortVal(b) - sortVal(a);
    return { sold: all.filter(m => m.direction === 'sold').sort(by), bought: all.filter(m => m.direction === 'bought').sort(by) };
  }, [data, prefs.sides, prefs.sort, zOf, ratioOf]);
  const qualCount = rows.sold.filter(qualifies).length + rows.bought.filter(qualifies).length;
  const clusters = data?.success ? data.clusters : [];

  const keyFor = useCallback((strike: number) => ladder.find(l => Math.abs(l.strike - strike) < 1e-9)?.key ?? strikeKey(strike), [ladder]);
  const keysInRange = useCallback((lo: number, hi: number) => {
    const ks = ladder.filter(l => l.strike >= lo - 1e-9 && l.strike <= hi + 1e-9).sort((a, b) => a.strike - b.strike).map(l => l.key);
    return ks.length ? ks : Array.from(new Set([strikeKey(lo), strikeKey(hi)]));
  }, [ladder]);
  const pickCluster = (cl: MoverCluster) => {
    const keys = keysInRange(cl.strike_lo, cl.strike_hi);
    if (onPickMany) onPickMany(keys); else { onPickOnly(keys[0]); keys.slice(1).forEach(onPick); }
  };

  // ── alerts: new qualifying mover / pattern → one in-app alert; storm-capped ──
  const seenRef = useRef<SeenMap>(loadSeen());
  const firedRef = useRef<number[]>([]);
  const suppressedRef = useRef<{ n: number; summarised: boolean }>({ n: 0, summarised: false });
  const [firedCount, setFiredCount] = useState(0);
  const prefsRef = useRef(prefs); prefsRef.current = prefs;
  const cRef = useRef(c); cRef.current = c;

  const fire = useCallback((message: string, key: string) => {
    const now = Date.now(); const p = prefsRef.current; const cc = cRef.current;
    firedRef.current = firedRef.current.filter(t => now - t < STORM_WINDOW_MS);
    if (firedRef.current.length >= STORM_MAX) {
      suppressedRef.current.n += 1;
      if (!suppressedRef.current.summarised) {
        suppressedRef.current.summarised = true;
        const msg = cc.summaryMsg(suppressedRef.current.n);
        raiseAlert(cc.alertTitle(underlying), msg, { sound: false, browser: false, key: `ft-movers-summary|${underlying}` });
        appendAlertLog({ id: `ftm_${now.toString(36)}_s`, ruleId: 'strike-movers', ruleName: cc.alertTitle(underlying), sourceId: 'rtl-flow', recordKey: `summary|${now}`, summary: msg, at: now });
      }
      return;
    }
    if (firedRef.current.length === 0) suppressedRef.current = { n: 0, summarised: false };
    firedRef.current.push(now);
    raiseAlert(cc.alertTitle(underlying), message, { sound: p.sound, browser: p.push, key: `ft-movers|${key}` });
    appendAlertLog({ id: `ftm_${now.toString(36)}_${firedRef.current.length}`, ruleId: 'strike-movers', ruleName: cc.alertTitle(underlying), sourceId: 'rtl-flow', recordKey: key, summary: message, at: now });
    setFiredCount(n => n + 1);
  }, [underlying]);

  const moverKey = useCallback((m: Mover) => `${underlying}|${session}|${strikeLabel(m.strike, m.side)}|${m.direction}`, [underlying, session]);
  const patternKey = useCallback((pt: MoverPattern) => (
    pt.kind === 'cluster_imbalance'
      ? `${underlying}|${session}|pattern|${pt.kind}|${strikeKey(pt.strike_lo)}|${strikeKey(pt.strike_hi)}|${pt.note_key}`
      : `${underlying}|${session}|pattern|${pt.kind}|${strikeKey(pt.call_strike)}|${strikeKey(pt.put_strike)}`
  ), [underlying, session]);
  const patternText = useCallback((pt: MoverPattern) => {
    if (pt.kind === 'cluster_imbalance') return c[pt.note_key](strikeKey(pt.strike_lo), strikeKey(pt.strike_hi), fmtImb(pt.imbalance));
    const dir = pt.kind === 'premium_bought_both_sides' ? 'bought' : 'sold';
    return c.patternMsg(strikeLabel(pt.call_strike, 'C'), strikeLabel(pt.put_strike, 'P'), c.dirWord[dir]);
  }, [c]);

  useEffect(() => {
    if (!data?.success || !prefs.alerts) return;
    const now = Date.now(); const seen = seenRef.current; let changed = false;
    const fresh = (k: string) => !seen[k] || now - seen[k] > SEEN_TTL_MS;
    const touch = (k: string) => { seen[k] = now; changed = true; };
    for (const m of [...rows.sold, ...rows.bought]) {
      if (!qualifies(m)) continue;
      const k = moverKey(m);
      if (fresh(k)) fire(c.moverMsg(underlying, strikeLabel(m.strike, m.side), c.dirWord[m.direction], fmtInt(magOf(m)), winMin, fmt1(m.spike_ratio, '×'), fmt1(zOf(m)), fmt1(ratioOf(m), '×')), k);
      touch(k);
    }
    for (const pt of data.patterns || []) {
      const k = patternKey(pt);
      if (fresh(k)) fire(patternText(pt), k);
      touch(k);
    }
    if (changed) saveSeen(seen);
  }, [data, prefs.alerts, rows, qualifies]);

  const toggleAlerts = () => {
    const next = !prefs.alerts;
    if (next) {
      // Baseline: what already qualifies is "seen", so only what appears from now on fires.
      const now = Date.now(); const seen = seenRef.current;
      for (const m of [...rows.sold, ...rows.bought]) if (qualifies(m)) seen[moverKey(m)] = now;
      for (const pt of data?.patterns || []) seen[patternKey(pt)] = now;
      saveSeen(seen);
    }
    set('alerts', next);
  };
  const pushAllowed = typeof Notification !== 'undefined' && Notification.permission === 'granted';

  // ── measures tooltip: hover (desktop) / long-press (touch) ──
  const panelRef = useRef<HTMLElement>(null);
  const [tip, setTip] = useState<Tip | null>(null);
  const tipTimer = useRef<number | null>(null);
  const clearTipTimer = () => { if (tipTimer.current !== null) { window.clearTimeout(tipTimer.current); tipTimer.current = null; } };
  const showTip = (key: string, el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    setTip({ key, x: r.left + r.width / 2, y: r.bottom });
  };
  const armTip = (key: string, el: HTMLElement, delay: number) => { clearTipTimer(); tipTimer.current = window.setTimeout(() => showTip(key, el), delay); };
  const hideTip = () => { clearTipTimer(); setTip(null); };
  useEffect(() => () => clearTipTimer(), []);
  const tipMover = useMemo(() => (tip ? [...rows.sold, ...rows.bought].find(m => `${m.side}${m.strike}` === tip.key) ?? null : null), [tip, rows]);
  useEffect(() => { if (tip && !tipMover) setTip(null); }, [tip, tipMover]);

  // ── slider (two overlaid ranges) ─────────────────────────
  const onFrom = (v: number) => setPrefs(p => { const to = p.mode === 'abs' ? p.to : win.to; return { ...p, mode: 'abs', from: Math.min(v, to - STEP), to }; });
  const onTo = (v: number) => setPrefs(p => { const from = p.mode === 'abs' ? p.from : win.from; return { ...p, mode: 'abs', to: Math.max(v, from + STEP), from }; });
  const pctL = (win.from / RTH_MIN) * 100, pctR = (win.to / RTH_MIN) * 100;

  const num = (v: string) => { const n = Number(v); return Number.isFinite(n) && n >= 0 ? n : 0; };
  const windowChip = `${hhmm(win.from)}–${hhmm(win.to)} · ${winMin}m`;
  const isWhole = prefs.mode === 'rel' && prefs.lenMin >= RTH_MIN;

  const renderRow = (m: Mover) => {
    const q = qualifies(m); const key = keyFor(m.strike); const isSel = selected.includes(key); const rk = `${m.side}${m.strike}`;
    const chg = m.change_net ?? (m.direction === 'sold' ? -(m.magnitude ?? 0) : (m.magnitude ?? 0));
    const imb = m.measures.imbalance;
    return (
      <div key={rk} className={'fm-row' + (q ? ' is-qual' : '') + (isSel ? ' is-sel' : '') + (tip?.key === rk ? ' is-tip' : '')}
        onClick={() => { hideTip(); onPick(key); }} onDoubleClick={(e) => { e.preventDefault(); hideTip(); onPickOnly(key); }}
        onMouseEnter={(e) => armTip(rk, e.currentTarget, TIP_HOVER_MS)} onMouseLeave={hideTip}
        onTouchStart={(e) => armTip(rk, e.currentTarget, TIP_PRESS_MS)} onTouchEnd={clearTipTimer} onTouchMove={clearTipTimer} onTouchCancel={hideTip}
        onContextMenu={(e) => { if (tip?.key === rk) e.preventDefault(); }}>
        <span className="fm-accent" />
        <span className="fm-strike fm-num">{strikeKey(m.strike)}<b className={'fm-side ' + m.side}>{m.side}</b></span>
        <span className={'fm-chg fm-num ' + signClass(chg)}>{fmtSigned(chg)}</span>
        <span className="fm-meta">
          <span className="fm-z"><i className="fm-lab">{c.colZ}</i><span className="fm-num">{fmt1(zOf(m))}</span></span>
          <span className="fm-spk"><i className="fm-lab">{c.colSpike}</i><span className="fm-num">{fmt1(m.spike_ratio, '×')}</span></span>
          <span className="fm-rat"><i className="fm-lab">{c.colRatio}</i><span className="fm-num">{fmt1(ratioOf(m), '×')}</span></span>
          <span className="fm-imbcell"><i className="fm-lab">{c.colImb}</i><ImbBar v={imb} /></span>
        </span>
        <span className="fm-t fm-num">{etTime(m.peak_t)}</span>
      </div>
    );
  };
  const renderGroup = (label: string, list: Mover[], kind: 'sold' | 'bought') => (
    <div className={'fm-group ' + kind}>
      <div className="fm-ghead"><span className={'fm-gdot ' + kind} />{label}<span className="fm-gcount fm-num">{list.length}</span></div>
      {list.length ? list.map(renderRow) : <div className="fm-gempty">—</div>}
    </div>
  );

  const renderTip = () => {
    if (!tip || !tipMover) return null;
    const m = tipMover;
    const lines: [string, string, string][] = [
      [c.tipChange, fmtSigned(m.change_net), signClass(m.change_net)],
      [c.tipSpike, fmt1(m.spike_ratio, '×'), ''],
      [c.tipPeak, etTime(m.peak_t), ''],
    ];
    for (const k of MEASURE_KEYS) {
      const v = m.measures[k];
      const txt = k === 'imbalance' ? fmtImb(v) : k === 'share_total' ? fmtPct(v) : k.startsWith('z_') ? fmt1(v) : fmt1(v, '×');
      const on = (k === Z_MEASURE[prefs.zmode] || k === RATIO_MEASURE[prefs.ratio]) ? ' is-on' : '';
      lines.push([c.measure[k], txt, (k === 'imbalance' ? signClass(v) : '') + on]);
    }
    if (m.cluster_id) lines.push([c.tipCluster, m.cluster_id, '']);
    const TIP_W = 236, TIP_H = 330;
    const vw = window.innerWidth, vh = window.innerHeight;
    const left = Math.max(8, Math.min(vw - 8 - TIP_W, tip.x - TIP_W / 2));
    const top = tip.y + 4 + TIP_H > vh ? Math.max(8, tip.y - 30 - TIP_H) : tip.y + 4;
    return (
      <div className="fm-tip" style={{ left, top, width: TIP_W }} role="tooltip" dir={isArabic ? 'rtl' : 'ltr'}>
        <div className="fm-tiphead fm-num">{strikeKey(m.strike)}<b className={'fm-side ' + m.side}>{m.side}</b><span className={'fm-pdir ' + m.direction}>{c[m.direction]}</span></div>
        {lines.map(([l, v, cls], i) => <div key={i} className={'fm-tipline' + (cls.includes('is-on') ? ' is-on' : '')}><span>{l}</span><b className={'fm-num ' + cls.replace('is-on', '')}>{v}</b></div>)}
        <div className="fm-tiphint">{c.clickHint}</div>
      </div>
    );
  };

  const renderClusters = () => {
    if (!clusters.length) return null;
    return (
      <div className={'fm-clusters' + (prefs.clusters ? '' : ' is-closed')}>
        <button type="button" className="fm-chead" onClick={() => set('clusters', !prefs.clusters)} aria-expanded={prefs.clusters}>
          <span>{c.clusters}</span><span className="fm-gcount fm-num">{clusters.length}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="fm-cchev"><path d="M6 9l6 6 6-6" /></svg>
        </button>
        {prefs.clusters && (
          <>
            <div className="fm-ccols"><span>{c.clusterCols.range}</span><span /><span>{c.clusterCols.net}</span><span>{c.clusterCols.share}</span><span>{c.clusterCols.imb}</span><span>{c.clusterCols.z}</span></div>
            {clusters.map(cl => {
              const strong = isNum(cl.imbalance) && Math.abs(cl.imbalance) >= STRONG_IMB;
              const keys = keysInRange(cl.strike_lo, cl.strike_hi);
              const isSel = keys.length > 0 && keys.every(k => selected.includes(k));
              const ref = cl.above_ref === null ? '' : cl.above_ref ? c.aboveRef : c.belowRef;
              return (
                <div key={cl.id} className={'fm-crow' + (strong ? ' is-strong' : '') + (isSel ? ' is-sel' : '') + ' ' + (isNum(cl.imbalance) && cl.imbalance < 0 ? 'neg' : 'pos')}
                  role="button" tabIndex={0} onClick={() => pickCluster(cl)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pickCluster(cl); } }}
                  title={`${c.strikesN(cl.n_strikes || keys.length)}${ref ? ' · ' + ref : ''} · ${c.clustersHint}`}>
                  <span className="fm-crange fm-num">{strikeKey(cl.strike_lo)}<em>–</em>{strikeKey(cl.strike_hi)}{cl.above_ref !== null && <small className={'fm-cref ' + (cl.above_ref ? 'up' : 'dn')} aria-hidden>{cl.above_ref ? '↑' : '↓'}</small>}</span>
                  <span className={'fm-cbias ' + cl.side_bias}>{c.bias[cl.side_bias]}</span>
                  <span className={'fm-cnet fm-num ' + signClass(cl.change_net)}>{fmtSigned(cl.change_net)}</span>
                  <span className="fm-cshare" title={fmtPct(cl.share_volume)}><i style={{ width: `${Math.max(0, Math.min(1, cl.share_volume ?? 0)) * 100}%` }} /></span>
                  <span className={'fm-cimb fm-num ' + signClass(cl.imbalance)}>{fmtImb(cl.imbalance)}</span>
                  <span className="fm-cz fm-num">{fmt1(cl.z_vs_clusters)}</span>
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  };

  const renderPattern = (pt: MoverPattern, i: number) => {
    if (pt.kind === 'cluster_imbalance') {
      const dir = pt.note_key === 'cluster_bought' ? 'bought' : 'sold';
      const cl = clusters.find(x => x.id === pt.cluster_id);
      return (
        <div key={i} className={'fm-pattern ' + dir}>
          <div className="fm-pstrikes fm-num">
            <button type="button" onClick={() => (cl ? pickCluster(cl) : onPickMany ? onPickMany(keysInRange(pt.strike_lo, pt.strike_hi)) : onPick(keyFor(pt.strike_lo)))}>
              {strikeKey(pt.strike_lo)}<span className="fm-pdash">–</span>{strikeKey(pt.strike_hi)}<b className={'fm-side ' + (pt.side_bias === 'puts' ? 'P' : pt.side_bias === 'calls' ? 'C' : 'M')}>{pt.side_bias === 'puts' ? 'P' : pt.side_bias === 'calls' ? 'C' : '±'}</b>
              <small className={signClass(pt.imbalance)}>{fmtImb(pt.imbalance)}</small>
            </button>
            <span className={'fm-pdir ' + dir}>{c[dir]}</span>
          </div>
          <div className="fm-pnote">{patternText(pt)}</div>
        </div>
      );
    }
    const dir = pt.kind === 'premium_bought_both_sides' ? 'bought' : 'sold';
    const cAmt = dir === 'sold' ? pt.call_sold : pt.call_bought, pAmt = dir === 'sold' ? pt.put_sold : pt.put_bought;
    return (
      <div key={i} className={'fm-pattern ' + dir}>
        <div className="fm-pstrikes fm-num">
          <button type="button" onClick={() => onPick(keyFor(pt.call_strike))}>{strikeKey(pt.call_strike)}<b className="fm-side C">C</b>{isNum(cAmt) ? <small>{fmtInt(cAmt)}</small> : null}</button>
          <span>+</span>
          <button type="button" onClick={() => onPick(keyFor(pt.put_strike))}>{strikeKey(pt.put_strike)}<b className="fm-side P">P</b>{isNum(pAmt) ? <small>{fmtInt(pAmt)}</small> : null}</button>
          <span className={'fm-pdir ' + dir}>{c[dir]}</span>
        </div>
        <div className="fm-pnote">{c[pt.note_key]}</div>
      </div>
    );
  };

  const status = (() => {
    if (!data) return loading ? c.loading : null;
    if (!data.success) return data.timeseriesdb_available === false ? c.tsdbDown : c.err;
    const st = data.stats; const im = st.imbalance;
    return (
      <>
        <span>{st.n_pairs} {c.strikes} · {c.mean} {fmtSigned(st.mean_change)} · {c.sd} {fmtInt(st.std_change)} · {st.intervals} {c.intervals}</span>
        {im && (isNum(im.calls_vs_puts) || isNum(im.above_vs_below)) && (
          <span className="fm-statimb">
            <span>{c.callsVsPuts} <b className={'fm-num ' + signClass(im.calls_vs_puts)}>{fmtImb(im.calls_vs_puts)}</b></span>
            <span>{c.aboveVsBelow} <b className={'fm-num ' + signClass(im.above_vs_below)}>{fmtImb(im.above_vs_below)}</b></span>
          </span>
        )}
      </>
    );
  })();
  const empty = data?.success && rows.sold.length === 0 && rows.bought.length === 0;

  if (!prefs.open) {
    return (
      <aside className="fm-panel is-collapsed" dir={isArabic ? 'rtl' : 'ltr'}>
        <button type="button" className="fm-expand" onClick={() => set('open', true)} title={c.expand} aria-label={c.expand}>
          <span className="fm-vlabel">{c.title}</span>
          {qualCount > 0 && <span className="fm-vcount fm-num">{qualCount}</span>}
          {prefs.alerts && <span className="fm-vbell" aria-hidden>●</span>}
        </button>
      </aside>
    );
  }

  return (
    <aside className="fm-panel" dir={isArabic ? 'rtl' : 'ltr'} aria-label={c.title} ref={panelRef}>
      <div className="fm-head">
        <div className="fm-titles">
          <div className="fm-title">{c.title}</div>
          <div className="fm-subrow"><span className="fm-sub">{c.sub}</span><span className={'fm-chip fm-num' + (isLive && prefs.mode === 'rel' ? ' is-live' : '')} title={c.window}><i />{windowChip}</span></div>
        </div>
        <button type="button" className="fm-iconbtn" onClick={() => set('open', false)} title={c.collapse} aria-label={c.collapse}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="fm-chev"><path d="M9 6l6 6-6 6" /></svg>
        </button>
      </div>

      <div className="fm-filters">
        <div className="fm-quick">
          {QUICK.map(n => <button key={n} type="button" className={'fm-pill' + (prefs.mode === 'rel' && prefs.lenMin === n ? ' on' : '')} onClick={() => setPrefs(p => ({ ...p, mode: 'rel', lenMin: n }))}>{c.quickMin(n)}</button>)}
          <button type="button" className={'fm-pill' + (isWhole ? ' on' : '')} onClick={() => setPrefs(p => ({ ...p, mode: 'rel', lenMin: RTH_MIN }))}>{c.session}</button>
        </div>
        <div className="fm-slider" title={c.window}>
          <div className="fm-track"><div className="fm-fill" style={{ left: `${pctL}%`, width: `${pctR - pctL}%` }} /></div>
          <input type="range" min={0} max={RTH_MIN} step={STEP} value={win.from} onChange={e => onFrom(Number(e.target.value))} aria-label="from" />
          <input type="range" min={0} max={RTH_MIN} step={STEP} value={win.to} onChange={e => onTo(Number(e.target.value))} aria-label="to" />
          <div className="fm-ticks fm-num"><span>09:30</span><span>11:00</span><span>12:30</span><span>14:00</span><span>16:00</span></div>
        </div>
        <div className="fm-lenses">
          <label className="fm-field"><span>{c.lens}</span>
            <select value={prefs.zmode} onChange={e => set('zmode', e.target.value as ZMode)}>
              {ZMODES.map(v => <option key={v} value={v}>{c.zmode[v]}</option>)}
            </select>
          </label>
          <label className="fm-field"><span>{c.ratio}</span>
            <select value={prefs.ratio} onChange={e => set('ratio', e.target.value as RatioMode)}>
              {RATIOS.map(v => <option key={v} value={v}>{c.ratioMode[v]}</option>)}
            </select>
          </label>
        </div>
        <div className="fm-thresholds">
          <label className="fm-field"><span>{c.minChange}</span><input type="number" min={0} step={50} value={prefs.minChange} onChange={e => set('minChange', num(e.target.value))} /><small>{c.contracts}</small></label>
          <label className="fm-field"><span>{c.minZ}</span><input type="number" min={0} step={0.5} value={prefs.minZ} onChange={e => set('minZ', num(e.target.value))} /></label>
          <label className="fm-field"><span>{c.minSpike}</span><input type="number" min={0} step={0.5} value={prefs.minSpike} onChange={e => set('minSpike', num(e.target.value))} /><small>{c.times}</small></label>
          <label className="fm-field"><span>{c.minRatio}</span><input type="number" min={0} step={0.5} value={prefs.minRatio} onChange={e => set('minRatio', num(e.target.value))} /><small>{c.times}</small></label>
        </div>
        <div className="fm-sideSort">
          <div className="fm-seg" role="group" aria-label="side">
            {([['C', c.calls], ['P', c.puts], ['CP', c.both]] as [SideFilter, string][]).map(([v, l]) => <button key={v} type="button" className={prefs.sides === v ? 'on' : ''} onClick={() => set('sides', v)}>{l}</button>)}
          </div>
          <label className="fm-sort"><span>{c.sort}</span>
            <select value={prefs.sort} onChange={e => set('sort', e.target.value as SortMode)}>
              {SORTS.map(v => <option key={v} value={v}>{c.sortBy[v]}</option>)}
            </select>
          </label>
        </div>
        <div className="fm-alerts">
          <button type="button" className={'fm-switch' + (prefs.alerts ? ' is-on' : '')} role="switch" aria-checked={prefs.alerts} onClick={toggleAlerts} title={c.alertHint}>
            <span className="fm-swtrack" /><span>{c.alertMe}</span>
            {prefs.alerts && firedCount > 0 && <span className="fm-fired fm-num">{firedCount}</span>}
          </button>
          {prefs.alerts && (
            <span className="fm-optins">
              <label><input type="checkbox" checked={prefs.sound} onChange={e => set('sound', e.target.checked)} />{c.sound}</label>
              <label title={pushAllowed ? '' : c.pushNeedsPermission} className={pushAllowed ? '' : 'is-off'}><input type="checkbox" disabled={!pushAllowed} checked={prefs.push && pushAllowed} onChange={e => set('push', e.target.checked)} />{c.push}</label>
            </span>
          )}
        </div>
      </div>

      <div className="fm-list" onScroll={hideTip}>
        <div className="fm-status">{status}</div>
        {empty && <div className="fm-empty">{qualCount === 0 && data?.stats.n_pairs ? c.none : c.noData}</div>}
        {!empty && data?.success && (
          <>
            <div className="fm-cols"><span /><span>{c.colStrike}</span><span>{c.colChange}</span><span>{c.colZ}</span><span>{c.colSpike}</span><span>{c.colRatio}</span><span>{c.colImb}</span><span>{c.colPeak}</span></div>
            {renderGroup(c.sold, rows.sold, 'sold')}
            {renderGroup(c.bought, rows.bought, 'bought')}
          </>
        )}
        {renderClusters()}
        {data?.success && (data.patterns || []).length > 0 && (
          <div className="fm-patterns">
            <div className="fm-phead">{c.patterns}</div>
            {data.patterns.map(renderPattern)}
          </div>
        )}
        <div className="fm-caption">{c.caption}</div>
        {renderTip()}
      </div>
    </aside>
  );
}
